import type { CSSProperties } from 'react';

/**
 * Wordmark "UNDERLINE" desenhada letra a letra (cada glifo é um path) para
 * permitir a entrada escalonada + underscore vermelho piscante.
 * Equivale ao InlineWordmark do protótipo (app/wordmark.jsx).
 */
const WORDMARK_PATHS: ReadonlyArray<string> = [
  'M8.97,12.44c-.83,0-1.67-.03-2.52-.09-1.9-.13-3.4-.55-4.49-1.25-1.31-.83-1.96-2.15-1.96-3.95V0h4.87v6.61c0,1.16.64,1.87,1.93,2.12.5.09,1.22.14,2.17.14.33,0,.69,0,1.06-.02.38-.01.74-.06,1.1-.12.36-.07.68-.18.97-.34s.52-.38.7-.67.26-.65.26-1.1V0h4.87v7.15c0,2.55-1.34,4.16-4.03,4.83-1.24.31-2.88.47-4.94.47h.01Z',
  'M37.59,12.03h-6.03l-7.03-7.1v7.1h-4.87V0h6.02l7.04,7.09V0h4.87v12.03Z',
  'M51.58,12.07l-11.7-.04V0h11.72c.89,0,1.76.14,2.61.43.85.29,1.55.68,2.08,1.16,1.34,1.24,2,2.71,2,4.42s-.67,3.18-2,4.42c-.53.49-1.23.88-2.08,1.16-.85.29-1.73.43-2.61.43l-.02.04h0ZM51.21,8.35c.53,0,1.01-.15,1.41-.44.53-.39.8-1.02.8-1.89s-.27-1.51-.8-1.9c-.41-.29-.88-.44-1.41-.44h-6.46v4.67h6.46Z',
  'M76.16,12.03h-17.13V0h16.99v3.52h-12.12v1.13h11.46v2.65h-11.46v1.19h12.26v3.53h0Z',
  'M95.98,12.03h-5.59c-.17-.14-.27-.34-.32-.59-.04-.26-.07-.53-.07-.83s-.02-.59-.07-.88c-.07-.41-.28-.67-.62-.78s-.84-.16-1.48-.16h-5.52v3.23h-4.87V0h12.96c1.99,0,3.37.45,4.13,1.34.34.4.55.8.64,1.21.09.4.13.77.13,1.1,0,1.39-.65,2.39-1.96,3.02,1.13.45,1.72,1.21,1.75,2.26s.13,1.79.28,2.22.35.72.6.88h.01ZM88.71,5.27c.45,0,.8-.05,1.05-.15s.42-.22.51-.36.14-.26.14-.36c0-.24-.07-.42-.21-.55-.24-.21-.73-.32-1.48-.32h-6.4v1.74h6.4-.01Z',
  'M112.87,12.03h-16.39V0h4.87v8.35h11.52s0,3.68,0,3.68Z',
  'M118.76,12.03h-4.87V0h4.87v12.03Z',
  'M138.98,12.03h-6.03l-7.03-7.1v7.1h-4.87V0h6.02l7.04,7.09V0h4.87v12.03h0Z',
  'M158.4,12.03h-17.13V0h16.99v3.52h-12.12v1.13h11.46v2.65h-11.46v1.19h12.26v3.53h0Z',
];

interface InlineWordmarkProps {
  height?: number;
  color?: string;
  animate?: boolean;
  /** atraso entre letras, em ms */
  stagger?: number;
  className?: string;
  style?: CSSProperties;
}

export function InlineWordmark({
  height = 18,
  color = 'currentColor',
  animate = true,
  stagger = 80,
  className,
  style,
}: InlineWordmarkProps) {
  const width = height * (183.8 / 12.45);
  return (
    <svg
      viewBox="0 0 183.8 12.45"
      width={width}
      height={height}
      className={className}
      style={{ display: 'block', overflow: 'visible', color, flexShrink: 0, ...style }}
      role="img"
      aria-label="Underline"
    >
      <g>
        {WORDMARK_PATHS.map((d, i) => (
          <path
            key={i}
            d={d}
            fill="currentColor"
            style={
              animate
                ? {
                    opacity: 0,
                    transformBox: 'fill-box',
                    transformOrigin: 'center bottom',
                    animation: `u-letter-in 0.42s var(--ease-back) ${i * stagger}ms both`,
                  }
                : undefined
            }
          />
        ))}
        <rect
          x="160.68"
          y="8.75"
          width="23.12"
          height="3.27"
          fill="var(--primary)"
          style={
            animate
              ? {
                  opacity: 0,
                  animation: `u-letter-in 0.32s var(--ease-back) ${WORDMARK_PATHS.length * stagger}ms both, u-blink 1.4s ease-in-out ${WORDMARK_PATHS.length * stagger + 320}ms infinite`,
                }
              : { animation: 'u-blink 1.4s ease-in-out infinite' }
          }
        />
      </g>
    </svg>
  );
}

export { WORDMARK_PATHS };
